import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import {
  useBusinessServiceRequest,
  useCurrentUserDateTimeFormat,
  useCurrentUserTimezone,
  useSetCurrentPageEntityLabel,
} from '@ingenico/react-section-utils';
import type { EmptyListProps } from '@ingenico/react-ui/notice-card';
import { useUserDateTimeFormat } from '@ingenico/react-utils/hooks';
import type { GridRowParams } from '@mui/x-data-grid';
import type { GridApiPremium } from '@mui/x-data-grid-premium';
import { toString } from 'lodash-es';
import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { DeviceManagementService } from '../../domain/business/services/deviceManagement.service';
import type { DeviceResourceDiagnosticsTestResult } from '../../domain/models/DeviceDiagnosticsDetails.model';
import {
  useGridPropsSetup,
  useInitialSetup,
  useLogicalDeviceDiagnosticsDetailsPageColumns,
} from './DeviceDiagnosticDetailsPage.hook';
import type { UseDeviceDiagnosticPageOutput } from './DeviceDiagnosticDetailsPage.model';

export function useDeviceDiagnosticPage(): UseDeviceDiagnosticPageOutput {
  const { logicalDeviceId, id, terminalId } = useParams<{ logicalDeviceId: string; id: string; terminalId: string }>();
  const [selectedRowElement, setSelectedRowElement] = useState<DeviceResourceDiagnosticsTestResult>();
  const setCurrentPageEntityLabel = useSetCurrentPageEntityLabel();
  const currentUserDateTimeFormat = useCurrentUserDateTimeFormat();
  const timezone = useCurrentUserTimezone();
  const { getUserDateAndTimeFormatWithTimezone } = useUserDateTimeFormat(currentUserDateTimeFormat);
  const { t, datagridRef, isTranslationReady } = useInitialSetup();

  // Load device details to get the device name for breadcrumb
  const logicalDeviceDetailsRequest = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService, isRefetch) =>
        deviceManagementService.getLogicalDeviceDetailsBff(toString(logicalDeviceId), { isRefetch }),
      [logicalDeviceId],
    ),
  );

  const deviceDiagnosticRequest = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback(
      (deviceManagementService) =>
        deviceManagementService.getDeviceDiagnosticDetailsByDeviceIdAndDiagnosticId({
          diagnosticId: toString(id),
          deviceId: logicalDeviceId ? toString(logicalDeviceId) : toString(terminalId),
        }),
      [id, logicalDeviceId, terminalId],
    ),
  );

  const { data, isError, isLoading } = useMemo(() => deviceDiagnosticRequest, [deviceDiagnosticRequest]);
  const [date, time] = getUserDateAndTimeFormatWithTimezone({
    instant: toString(data?.date),
    timezone,
  });
  const displayDate = `${date} : ${time}`;

  const { columns } = useLogicalDeviceDiagnosticsDetailsPageColumns();

  useEffect(() => {
    if (logicalDeviceDetailsRequest.data?.name) {
      setCurrentPageEntityLabel(logicalDeviceDetailsRequest.data.name);
    }
  }, [logicalDeviceDetailsRequest.data?.name, setCurrentPageEntityLabel]);

  const handleRowClick = useCallback(
    ({ row }: GridRowParams<DeviceResourceDiagnosticsTestResult>) => {
      setSelectedRowElement(row);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  const handleCloseDrawer = useCallback(() => setSelectedRowElement(undefined), []);

  const drawer = useMemo<UseDeviceDiagnosticPageOutput['drawer']>(
    () => ({
      isOpen: !!selectedRowElement,
      handleCloseDrawer,
      testResultDetails: selectedRowElement,
    }),
    [handleCloseDrawer, selectedRowElement],
  );

  const total = useMemo(() => (data ? data.testResults.length : 0), [data]);

  const getRowId = useCallback(
    (element: DeviceResourceDiagnosticsTestResult) =>
      `${element.module}-${element.executionStatus}-${element.resultCode}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data],
  );

  const shouldDisplayGrid = useMemo(
    () => (deviceDiagnosticRequest.isSuccess || isLoading) && isTranslationReady,
    [deviceDiagnosticRequest.isSuccess, isLoading, isTranslationReady],
  );

  const gridProps = useGridPropsSetup({
    handleRowClick,
    datagridRef: datagridRef as MutableRefObject<GridApiPremium>,
    total,
    getRowId,
    isLoading,
    shouldDisplayGrid,
  });

  const noRowsProps = useMemo<EmptyListProps>(
    () => ({
      image: 'empty-services',
      messageLabel: 'emptyDeviceDiagnosticsLogs.message',
      namespace: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE,
      titleLabel: '',
      isBordered: false,
    }),
    [],
  );

  return {
    data,
    drawer,
    date: displayDate,
    isError,
    isLoading,
    noRowsProps,
    gridProps,
    columns,
    t,
  };
}
